import React, { useState, useEffect } from "react";
import "./CreateTask.css";
import MenuBar from "./MenuBar";
import Footer from "./Footer";
import {
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
  fetchVehicleMakes,
  fetchVehicleModels,
  formatVehicleLabel,
  getYearOptions,
  sanitizeVinInput,
  sanitizeYearInput,
} from "../utils/vehicleLookup";


// import { LocalizationProvider, DateTimePicker } from "@mui/x-date-pickers";
// import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
// import moment from "moment";

const envVariable = require("../EnvironmentVariables.json");

// Format a Date object as UTC datetime string for sending to backend
const formatDateTimeUTC = (datetime) => {
  if (!datetime) return null;
  const d = new Date(datetime);
  if (isNaN(d.getTime())) return null;

  const year = d.getUTCFullYear();
  const month = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  const hours = String(d.getUTCHours()).padStart(2, "0");
  const minutes = String(d.getUTCMinutes()).padStart(2, "0");
  const seconds = String(d.getUTCSeconds()).padStart(2, "0");

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};


// Debounce helper
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
};

const CreateTask = () => {
  const navigate = useNavigate();
  const [mechanics, setMechanics] = useState([]);
  const [errors, setErrors] = useState({});
  const [vehicleApiError, setVehicleApiError] = useState("");
  const [makeOptions, setMakeOptions] = useState([]);
  const [vehicleOptionsLoading, setVehicleOptionsLoading] = useState({
    makes: false,
    walkInModels: false,
    newVehicleModels: false,
    editVehicleModels: false,
  });
  const [walkInModelOptions, setWalkInModelOptions] = useState([]);
  const [newVehicleModelOptions, setNewVehicleModelOptions] = useState([]);
  const [editVehicleModelOptions, setEditVehicleModelOptions] = useState([]);
  const yearOptions = getYearOptions();

  // Customer lookup state
  const [customerEmail, setCustomerEmail] = useState("");
  const [isLookingUp, setIsLookingUp] = useState(false);
  const [customerFound, setCustomerFound] = useState(null); // null = not searched yet, object = found, false = not found
  const [customerVehicles, setCustomerVehicles] = useState([]);
  const debouncedEmail = useDebounce(customerEmail, 500);
 
  // Task form state
  const [newTask, setNewTask] = useState({
    customerName: "",
    phoneNumber: "",
    issueDescription: "",
    notes: "",
    faultyParts: "",
    calendarStart: "",
    calendarEnd: "",
    mechanicId: "",
    userCarId: "", // Selected vehicle from customer's account
    carModel: "",  // For manual entry
    carMake: "",
    carYear: "",
    vinNumber: "",
  });

  // Add new vehicle dialog
  const [addVehicleOpen, setAddVehicleOpen] = useState(false);
  const [newVehicle, setNewVehicle] = useState({
    car_name: "",
    make: "",
    model: "",
    year: "",
    submodel: "",
    vin_number: "",
  });

  // Edit vehicle state
  const [editVehicleOpen, setEditVehicleOpen] = useState(false);
  const [editVehicle, setEditVehicle] = useState({
    user_car_id: null,
    car_name: "",
    make: "",
    model: "",
    year: "",
    submodel: "",
    vin_number: "",
  });

  // Fetch mechanics list on component mount
  useEffect(() => {
    const fetchMechanics = async () => {
      const SC_Id = localStorage.getItem("SC_Id");
      if (!SC_Id) return;

      try {
        const response = await fetch(
          `${envVariable.serverId}/SCO/Mechanic.php?SC_Id=${SC_Id}`,
          {
            method: "GET",
            headers: { "Content-Type": "application/json" },
          }
        );
        const data = await response.json();
        if (data?.mechanics) {
          setMechanics(data.mechanics);
        }
      } catch (error) {
        console.error("Error fetching mechanics:", error);
      }
    };
    fetchMechanics();
  }, []);

  // Auto-lookup when email changes (debounced)
  useEffect(() => {
    const lookupCustomer = async () => {
      if (!debouncedEmail.trim()) {
        setCustomerFound(null);
        setCustomerVehicles([]);
        return;
      }

      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailPattern.test(debouncedEmail.trim())) {
        return; // Don't lookup invalid emails
      }

      setIsLookingUp(true);

      try {
        const response = await fetch(
          `${envVariable.serverId}/SCO/lookupCustomerByEmail.php?email=${encodeURIComponent(debouncedEmail.trim())}`,
          {
            method: "GET",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
          }
        );

        if (!response.ok) {
          console.error("Lookup failed with status:", response.status);
          setCustomerFound(false);
          return;
        }

        const data = await response.json();

        if (data.type === "success") {
          if (data.found) {
            setCustomerFound(data.customer);
            setCustomerVehicles(data.vehicles || []);
            // Auto-fill customer info
            setNewTask((prev) => ({
              ...prev,
              customerName: `${data.customer.first_name || ""} ${data.customer.last_name || ""}`.trim(),
              phoneNumber: data.customer.phone || "",
              userCarId: "", // Reset vehicle selection
              carModel: "",
              vinNumber: "",
            }));
          } else {
            setCustomerFound(false);
            setCustomerVehicles([]);
          }
        }
      } catch (error) {
        console.error("Error looking up customer:", error);
        setCustomerFound(false);
      } finally {
        setIsLookingUp(false);
      }
    };

    lookupCustomer();
  }, [debouncedEmail]);

  const loadModelOptions = async (year, make, stateKey, setter, ignoreRef) => {
    if (!year || year.length !== 4 || !make.trim()) {
      setter([]);
      return;
    }

    setVehicleOptionsLoading((prev) => ({ ...prev, [stateKey]: true }));
    setVehicleApiError("");

    try {
      const models = await fetchVehicleModels(year, make);
      if (!ignoreRef.current) {
        setter(models);
      }
    } catch (error) {
      console.error("Error fetching vehicle models:", error);
      if (!ignoreRef.current) {
        setVehicleApiError("Vehicle model lookup could not be loaded. Manual entry still works.");
        setter([]);
      }
    } finally {
      if (!ignoreRef.current) {
        setVehicleOptionsLoading((prev) => ({ ...prev, [stateKey]: false }));
      }
    }
  };

  useEffect(() => {
    let ignore = false;

    const loadMakes = async () => {
      setVehicleOptionsLoading((prev) => ({ ...prev, makes: true }));
      setVehicleApiError("");

      try {
        const makes = await fetchVehicleMakes();
        if (!ignore) {
          setMakeOptions(makes);
        }
      } catch (error) {
        console.error("Error fetching vehicle makes:", error);
        if (!ignore) {
          setVehicleApiError("Vehicle lookup could not be loaded. Manual entry still works.");
        }
      } finally {
        if (!ignore) {
          setVehicleOptionsLoading((prev) => ({ ...prev, makes: false }));
        }
      }
    };

    loadMakes();

    return () => {
      ignore = true;
    };
  }, []);

  useEffect(() => {
    const ignoreRef = { current: false };
    loadModelOptions(newTask.carYear, newTask.carMake, "walkInModels", setWalkInModelOptions, ignoreRef);
    return () => {
      ignoreRef.current = true;
    };
  }, [newTask.carYear, newTask.carMake]);

  useEffect(() => {
    const ignoreRef = { current: false };
    loadModelOptions(newVehicle.year, newVehicle.make, "newVehicleModels", setNewVehicleModelOptions, ignoreRef);
    return () => {
      ignoreRef.current = true;
    };
  }, [newVehicle.year, newVehicle.make]);

  useEffect(() => {
    const ignoreRef = { current: false };
    loadModelOptions(editVehicle.year, editVehicle.make, "editVehicleModels", setEditVehicleModelOptions, ignoreRef);
    return () => {
      ignoreRef.current = true;
    };
  }, [editVehicle.year, editVehicle.make]);

  const updateWalkInVehicleField = (field, rawValue) => {
    const value =
      field === "carYear" ? sanitizeYearInput(rawValue) : rawValue;

    setNewTask((prev) => {
      const nextTask = { ...prev, [field]: value };

      if (field === "carYear") {
        nextTask.carMake = "";
        nextTask.carModel = "";
      }

      if (field === "carMake") {
        nextTask.carModel = "";
      }

      return nextTask;
    });
  };

  const updateVehicleDraft = (setter, field, rawValue) => {
    setter((prev) => {
      const value =
        field === "year"
          ? sanitizeYearInput(rawValue)
          : field === "vin_number"
            ? sanitizeVinInput(rawValue)
            : rawValue;

      const nextVehicle = { ...prev, [field]: value };

      if (field === "year") {
        nextVehicle.make = "";
        nextVehicle.model = "";
        nextVehicle.submodel = "";
      }

      if (field === "make") {
        nextVehicle.model = "";
        nextVehicle.submodel = "";
      }

      return nextVehicle;
    });
  };

  // Add new vehicle for existing customer
  const handleAddVehicle = async () => {
    if (!customerFound?.user_id) return;

    try {
      const response = await fetch(
        `${envVariable.serverId}/SCO/addCustomerVehicle.php`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            user_id: customerFound.user_id,
            ...newVehicle,
          }),
        }
      );
      const data = await response.json();

      if (data.type === "success") {
        // Add the new vehicle to the list and select it
        const vehicle = data.vehicle || {
          user_car_id: data.user_car_id,
          ...newVehicle,
          display_name: newVehicle.car_name || `${newVehicle.year} ${newVehicle.make} ${newVehicle.model}`.trim() || "New Vehicle",
        };
        setCustomerVehicles((prev) => [vehicle, ...prev]);
        setNewTask((prev) => ({ ...prev, userCarId: data.user_car_id }));
        setAddVehicleOpen(false);
        setNewVehicle({ car_name: "", make: "", model: "", year: "", submodel: "", vin_number: "" });
      } else {
        alert(data.message || "Failed to add vehicle");
      }
    } catch (error) {
      console.error("Error adding vehicle:", error);
      alert("Failed to add vehicle");
    }
  };

  // Open edit vehicle form with selected vehicle's data
  const openEditVehicleForm = () => {
    const selectedVehicle = customerVehicles.find(
      (v) => String(v.user_car_id) === String(newTask.userCarId)
    );
    if (selectedVehicle) {
      setEditVehicle({
        user_car_id: selectedVehicle.user_car_id,
        car_name: selectedVehicle.car_name || "",
        make: selectedVehicle.make || "",
        model: selectedVehicle.model || "",
        year: selectedVehicle.year || "",
        submodel: selectedVehicle.submodel || "",
        vin_number: selectedVehicle.vin_number || "",
      });
      setEditVehicleOpen(true);
      setAddVehicleOpen(false);
    }
  };

  // Handle editing an existing vehicle
  const handleEditVehicle = async () => {
    if (!editVehicle.user_car_id) return;

    if (!editVehicle.car_name.trim()) {
      alert("Vehicle name is required");
      return;
    }

    try {
      const response = await fetch(
        `${envVariable.serverId}/SCO/editCustomerVehicle.php`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(editVehicle),
        }
      );
      const data = await response.json();

      if (data.type === "success") {
        // Update the vehicle in the list
        const updatedVehicle = data.vehicle || editVehicle;
        setCustomerVehicles((prev) =>
          prev.map((v) =>
            v.user_car_id === updatedVehicle.user_car_id ? { ...v, ...updatedVehicle } : v
          )
        );
        setEditVehicleOpen(false);
        setEditVehicle({ user_car_id: null, car_name: "", make: "", model: "", year: "", submodel: "", vin_number: "" });
      } else {
        alert(data.message || "Failed to update vehicle");
      }
    } catch (error) {
      console.error("Error updating vehicle:", error);
      alert("Failed to update vehicle");
    }
  };

  const validateInputs = () => {
    const newErrors = {};

    // Email is optional but must be valid if provided
    if (customerEmail.trim()) {
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailPattern.test(customerEmail.trim())) {
        newErrors.customerEmail = "Please enter a valid email address.";
      }
    }

    // Name required if no customer found
    if (!customerFound) {
      if (!newTask.customerName.trim()) {
        newErrors.customerName = "Customer name is required.";
      }
    }

    if (!newTask.issueDescription.trim()) {
      newErrors.issueDescription = "Issue description is required.";
    }

    if (!customerFound) {
      if (!newTask.carYear || newTask.carYear.length !== 4) {
        newErrors.carYear = "Year must be a 4-digit number.";
      }

      if (!newTask.carMake.trim()) {
        newErrors.carMake = "Car make is required.";
      }

      if (!newTask.carModel.trim()) {
        newErrors.carModel = "Car model is required.";
      }
    }

    if (!newTask.calendarStart) {
      newErrors.calendarStart = "Start time is required.";
    }

    if (!newTask.calendarEnd) {
      newErrors.calendarEnd = "End time is required.";
    }

    if (newTask.calendarStart && newTask.calendarEnd) {
      const startDate = new Date(newTask.calendarStart);
      const endDate = new Date(newTask.calendarEnd);
      if (endDate <= startDate) {
        newErrors.calendarEnd = "End time must be after start time.";
      }
    }

    // Vehicle is optional - customer may not have one yet
    // Car model is also optional for walk-ins without detailed vehicle info

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const [endManuallySet, setEndManuallySet] = useState(false);

  const pad = (n) => String(n).padStart(2, "0");

  const formatLocal = (date) => {
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateInputs()) return;

    try {
      const SC_Id = localStorage.getItem("SC_Id");
      if (!SC_Id) return;

      // Validate mechanic availability if a mechanic is selected
      // Uses same endpoint as Calendar for consistency
      if (newTask.mechanicId) {
        const availabilityCheck = await fetch(
          `${envVariable.serverId}/mechanicSlots/checkSlotsValid.php`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({
              mechanic_id: newTask.mechanicId,
              sc_id: SC_Id,
              calendar_start: formatDateTimeUTC(newTask.calendarStart),
              calendar_end: formatDateTimeUTC(newTask.calendarEnd),
            }),
          }
        );

        const availResult = await availabilityCheck.json();

        if (availResult.type === "failure" || availResult.type === "error") {
          setErrors({ mechanicId: availResult.message || "Mechanic is not available for this time slot" });
          return;
        }
      }

      // Build payload
      const payload = {
        SC_Id,
        status: "Open",
        issueDescription: newTask.issueDescription,
        notes: newTask.notes,
        faulty_parts: newTask.faultyParts,
        calendarStart: formatDateTimeUTC(newTask.calendarStart),
        calendarEnd: formatDateTimeUTC(newTask.calendarEnd),
        mechanicId: newTask.mechanicId || null,
      };

      if (customerFound) {
        // Linked to existing customer account
        payload.customerName = newTask.customerName || `${customerFound.first_name} ${customerFound.last_name}`.trim();
        payload.phoneNumber = newTask.phoneNumber || customerFound.phone;
        payload.userCarId = newTask.userCarId;
        // Get car info from selected vehicle
        const selectedVehicle = customerVehicles.find(
          (v) => String(v.user_car_id) === String(newTask.userCarId)
        );
        payload.carModelName = selectedVehicle?.model || "";
        payload.carModel =
          formatVehicleLabel(selectedVehicle || {}) ||
          selectedVehicle?.display_name ||
          selectedVehicle?.model ||
          "";
        payload.carMake = selectedVehicle?.make || "";
        payload.carYear = selectedVehicle?.year || "";
        payload.vinNumber = selectedVehicle?.vin_number || "";
      } else {
        // New customer / walk-in
        payload.customerName = newTask.customerName;
        payload.phoneNumber = newTask.phoneNumber;
        payload.carYear = newTask.carYear;
        payload.carMake = newTask.carMake.trim();
        payload.carModelName = newTask.carModel.trim();
        payload.carModel = formatVehicleLabel({
          year: newTask.carYear,
          make: newTask.carMake,
          model: newTask.carModel,
        });
        payload.vinNumber = sanitizeVinInput(newTask.vinNumber);
      }

      const response = await fetch(envVariable.serverId + "/SCO/AddTask.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      let data;
      try {
        data = await response.json();
      } catch {
        data = { status: "Success" };
      }

      if (!response.ok || data.status !== "Success") {
        throw new Error(data.message || "Failed to create task");
      }

      navigate("/tasks");
    } catch (error) {
      console.error("Error creating task:", error);
      setErrors({ submit: error.message || "Failed to create task" });
    }
  };

  // Determine if customer info fields should be editable
  const isCustomerInfoLocked = customerFound && (customerFound.first_name || customerFound.last_name);
  const [showAdvanced, setShowAdvanced] = useState(false); // Toggle for progressive disclosure

  return (
    <>
      <MenuBar />
      <div className="create-task-wrapper">
        <h2 className="create-task-title">Create a New Task</h2>

        {errors.submit && (
          <Alert severity="error" style={{ marginBottom: "20px" }}>
            {errors.submit}
          </Alert>
        )}

        {vehicleApiError && (
          <Alert severity="info" style={{ marginBottom: "15px" }}>
            {vehicleApiError}
          </Alert>
        )}

        <form className="create-task-form" onSubmit={handleSubmit}>
          {/* Customer Email - Primary lookup field */}
          <div className="form-field" style={{ marginBottom: "15px" }}>
            <label className="datetime-label">Customer Email</label>
            <div style={{ position: "relative" }}>
              <input
                type="email"
                placeholder="Enter customer email (optional - for account linking)"
                value={customerEmail}
                onChange={(e) => {
                  setCustomerEmail(e.target.value);
                  if (!e.target.value.trim()) {
                    setCustomerFound(null);
                    setCustomerVehicles([]);
                    setNewTask((prev) => ({
                      ...prev,
                      customerName: "",
                      phoneNumber: "",
                      userCarId: "",
                      carYear: "",
                      carMake: "",
                      carModel: "",
                      vinNumber: "",
                    }));
                  }
                }}
                style={{ paddingRight: "40px" }}
              />
              {isLookingUp && (
                <CircularProgress
                  size={20}
                  style={{ position: "absolute", right: "10px", top: "50%", marginTop: "-10px" }}
                />
              )}
            </div>
            {errors.customerEmail && (
              <span className="input-error">{errors.customerEmail}</span>
            )}

            {/* Customer Found Indicator */}
            {customerFound && (
              <Alert severity="success" style={{ marginTop: "8px" }}>
                Found: <strong>{customerFound.first_name} {customerFound.last_name}</strong>
                {customerFound.phone && ` (${customerFound.phone})`}
                {customerFound.is_guest && " - Guest account"}
              </Alert>
            )}

            {/* Customer Not Found Indicator */}
            {customerFound === false && customerEmail.trim() && (
              <Alert severity="info" style={{ marginTop: "8px" }}>
                No existing customer found. Enter details below for a new customer.
              </Alert>
            )}
          </div>

          {/* Customer Name & Phone */}
          <div className="form-row">
            <div className="form-field">
              <label className="datetime-label">
                Customer Name {!customerFound && "*"}
                {isCustomerInfoLocked && " (from account)"}
              </label>
              <input
                type="text"
                placeholder="Customer Name"
                value={newTask.customerName}
                onChange={(e) =>
                  setNewTask({ ...newTask, customerName: e.target.value })
                }
                readOnly={isCustomerInfoLocked}
                style={isCustomerInfoLocked ? { backgroundColor: "#f5f5f5" } : {}}
              />
              {errors.customerName && (
                <span className="input-error">{errors.customerName}</span>
              )}
            </div>
            <div className="form-field">
              <label className="datetime-label">
                Phone Number
                {isCustomerInfoLocked && customerFound.phone && " (from account)"}
              </label>
              <input
                type="text"
                placeholder="Phone Number (10 digits)"
                value={newTask.phoneNumber}
                onChange={(e) =>
                  setNewTask({ ...newTask, phoneNumber: e.target.value })
                }
                readOnly={isCustomerInfoLocked && customerFound.phone}
                style={isCustomerInfoLocked && customerFound.phone ? { backgroundColor: "#f5f5f5" } : {}}
              />
              {errors.phoneNumber && (
                <span className="input-error">{errors.phoneNumber}</span>
              )}
            </div>
          </div>


          <div style={{ margin: "15px 0" }}>
            <Button
              variant="text"
              onClick={() => setShowAdvanced(prev => !prev)}
            >
              {showAdvanced ? "Hide advanced options" : "Show advanced options"}
            </Button>
          </div>

          {/* Vehicle Selection (for existing customers) */}
          {customerFound && (
            <div className="form-row">
              <div className="form-field" style={{ flex: 2 }}>
                <FormControl fullWidth size="small">
                  <InputLabel id="vehicle-select-label">Select Vehicle *</InputLabel>
                  <Select
                    labelId="vehicle-select-label"
                    value={newTask.userCarId}
                    label="Select Vehicle *"
                    onChange={(e) =>
                      setNewTask({ ...newTask, userCarId: e.target.value })
                    }
                  >
                    <MenuItem value="">-- Select a vehicle --</MenuItem>
                    {customerVehicles.map((vehicle) => (
                      <MenuItem key={vehicle.user_car_id} value={vehicle.user_car_id}>
                        {vehicle.display_name || `${vehicle.year || ""} ${vehicle.make || ""} ${vehicle.model || ""}`.trim() || "Unnamed Vehicle"}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {errors.userCarId && (
                  <span className="input-error">{errors.userCarId}</span>
                )}
              </div>
              {/* Only show Add/Edit Vehicle buttons for new customers (not in system) */}
              {showAdvanced && !isCustomerInfoLocked && (
                <div style={{ display: "flex", gap: "10px" }}>
                  <Button
                    variant="outlined"
                    onClick={() => setAddVehicleOpen(true)}
                    style={{ height: "40px" }}
                  >
                    + Add Vehicle
                  </Button>
                  {newTask.userCarId && (
                    <Button
                      variant="outlined"
                      color="warning"
                      onClick={openEditVehicleForm}
                      style={{ height: "40px" }}
                    >
                      Edit Vehicle
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Manual Vehicle Entry (for new customers) */}
          {!customerFound && (
            <>
            <div className="form-row">
              <div className="form-field">
                <label className="datetime-label">Year *</label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  list="vehicle-year-options"
                  placeholder="e.g., 2020"
                  value={newTask.carYear}
                  onChange={(e) =>
                    updateWalkInVehicleField("carYear", e.target.value)
                  }
                  maxLength={4}
                />
                <datalist id="vehicle-year-options">
                  {yearOptions.map((year) => (
                    <option key={year} value={year} />
                  ))}
                </datalist>
                {errors.carYear && (
                  <span className="input-error">{errors.carYear}</span>
                )}
              </div>
              <div className="form-field">
                <label className="datetime-label">Car Make *</label>
                <Autocomplete
                  freeSolo
                  options={makeOptions}
                  loading={vehicleOptionsLoading.makes}
                  value={newTask.carMake}
                  onInputChange={(_, value) => updateWalkInVehicleField("carMake", value)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="e.g., Toyota"
                      error={!!errors.carMake}
                      helperText={errors.carMake || "Select from suggestions or type your own"}
                    />
                  )}
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-field">
                <label className="datetime-label">Car Model *</label>
                <Autocomplete
                  freeSolo
                  options={walkInModelOptions}
                  loading={vehicleOptionsLoading.walkInModels}
                  value={newTask.carModel}
                  onInputChange={(_, value) => updateWalkInVehicleField("carModel", value)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="e.g., Camry"
                      error={!!errors.carModel}
                      helperText={errors.carModel || "Select from suggestions or type your own"}
                    />
                  )}
                />
              </div>

              
              {showAdvanced &&(

              <div className="form-field">
                <label className="datetime-label" style={{color: "#3F51B5"}}>VIN Number (Optional)</label>
                <input
                  
                  type="text"
                  placeholder="17-character VIN"
                  value={newTask.vinNumber}
                  onChange={(e) =>
                    setNewTask({ ...newTask, vinNumber: sanitizeVinInput(e.target.value) })
                  }
                  maxLength={17}
                />
              </div>
                )}
            </div>
            </>
          )}



          {/* Scheduling */}
          <div className="form-row">
            {/* START TIME */}
            <div className="form-field">
              <label className="datetime-label">Start Time *</label>
              <input
                type="datetime-local"
                value={newTask.calendarStart}
                onChange={(e) => {
                  const start = e.target.value;
                  const startDate = new Date(start);

                  let end = newTask.calendarEnd;

                  // Only auto-update if user hasn't manually set end
                  if (!endManuallySet) {
                    const newEnd = new Date(startDate);
                    newEnd.setHours(newEnd.getHours() + 1);
                    end = formatLocal(newEnd);
                  }

                  setNewTask({
                    ...newTask,
                    calendarStart: start,
                    calendarEnd: end,
                  });
                }}
              />
              {errors.calendarStart && (
                <span className="input-error">{errors.calendarStart}</span>
              )}
            </div>

            {/* END TIME */}
            <div className="form-field">
              <label className="datetime-label">End Time *</label>
              <input
                type="datetime-local"
                value={newTask.calendarEnd}
                min={newTask.calendarStart}
                onChange={(e) => {
                  const newEnd = e.target.value;

                  // user manually changed end → disable auto-sync
                  if (!newEnd) {
                    setEndManuallySet(false);
                  } else {
                    const startDate = new Date(newTask.calendarStart);
                    const endDate = new Date(newEnd);

                    const diffHours = (endDate - startDate) / (1000 * 60 * 60);

                    //If exactly 1 hr → go back to auto mode
                    setEndManuallySet(diffHours !== 1);
                  }

                  setNewTask({
                    ...newTask,
                    calendarEnd: newEnd,
                  });
                }}
              />
              {errors.calendarEnd && (
                <span className="input-error">{errors.calendarEnd}</span>
              )}
            </div>
          </div>

          {/* Mechanic Assignment */}
          {showAdvanced && (
            <div className="form-row">
            <div className="form-field">
              <FormControl fullWidth size="small" error={!!errors.mechanicId}>
                <InputLabel id="mechanic-select-label" style={{color: "#3F51B5"}}>Assign Mechanic (Optional)</InputLabel>
                <Select
                  labelId="mechanic-select-label"
                  value={newTask.mechanicId}
                  label="Assign Mechanic (Optional)"
                  onChange={(e) =>
                    setNewTask({ ...newTask, mechanicId: e.target.value })
                  }
                >
                  <MenuItem value="">Unassigned</MenuItem>
                  {mechanics.map((mech) => (
                    <MenuItem key={mech.user_id} value={mech.user_id}>
                      {mech.first_name} {mech.last_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {errors.mechanicId && (
                <span className="input-error">{errors.mechanicId}</span>
              )}
            </div>
          </div>
          )}
          

          {/* Issue Description */}
          <div className="form-field">
            <label className="datetime-label">Issue Description *</label>
            <textarea
              placeholder="Describe the service needed..."
              value={newTask.issueDescription}
              onChange={(e) =>
                setNewTask({ ...newTask, issueDescription: e.target.value })
              }
              className="issue-description"
            />
            {errors.issueDescription && (
              <span className="input-error">{errors.issueDescription}</span>
            )}
          </div>

          <div style={{ display: "flex", gap: "16px" }}>
            {/* Notes */}
            <div className="form-field">
              <label className="datetime-label">Notes</label>
              <textarea
                placeholder="Internal notes (optional)..."
                value={newTask.notes}
                onChange={(e) => setNewTask({ ...newTask, notes: e.target.value })}
                className="issue-description" //same styling as issue description (textbox)
              />
            </div>

            {/* Faulty / Replaced Parts */}
            <div className="form-field">
              <label className="datetime-label">Faulty / Replaced Parts</label>
              <textarea
                placeholder="List faulty or replaced parts..."
                value={newTask.faultyParts}
                onChange={(e) =>
                  setNewTask({ ...newTask, faultyParts: e.target.value })
                }
                className="issue-description"
              />
            </div>
          </div>

          {/* Submit Buttons */}
          <div className="form-buttons">
            <Button
              type="button"
              onClick={() => navigate("/tasks")}
              color="secondary"
            >
              Cancel
            </Button>
            <Button type="submit" variant="contained" color="primary">
              Create Task
            </Button>
          </div>
        </form>
      </div>

      {/* Add Vehicle Dialog */}
      <Dialog open={addVehicleOpen} onClose={() => setAddVehicleOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Vehicle for {customerFound?.first_name} {customerFound?.last_name}</DialogTitle>
        <DialogContent>
          <div style={{ display: "flex", flexDirection: "column", gap: "15px", paddingTop: "10px" }}>
            <TextField
              label="Vehicle Name *"
              value={newVehicle.car_name}
              onChange={(e) => setNewVehicle({ ...newVehicle, car_name: e.target.value })}
              placeholder="e.g., My Daily Driver, Work Truck"
              required
              error={!newVehicle.car_name.trim()}
              helperText={!newVehicle.car_name.trim() ? "Vehicle name is required" : ""}
            />
            <TextField
              label="Year"
              value={newVehicle.year}
              onChange={(e) => updateVehicleDraft(setNewVehicle, "year", e.target.value)}
              placeholder="e.g., 2020"
              inputProps={{ inputMode: "numeric", maxLength: 4 }}
            />
            <Autocomplete
              freeSolo
              options={makeOptions}
              loading={vehicleOptionsLoading.makes}
              value={newVehicle.make}
              onInputChange={(_, value) => updateVehicleDraft(setNewVehicle, "make", value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Make"
                  placeholder="e.g., Toyota"
                  helperText="Select from suggestions or type your own"
                />
              )}
            />
            <Autocomplete
              freeSolo
              options={newVehicleModelOptions}
              loading={vehicleOptionsLoading.newVehicleModels}
              value={newVehicle.model}
              onInputChange={(_, value) => updateVehicleDraft(setNewVehicle, "model", value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Model"
                  placeholder="e.g., Camry"
                  helperText="Select from suggestions or type your own"
                />
              )}
            />
            <TextField
              label="Submodel/Trim (Optional)"
              value={newVehicle.submodel}
              onChange={(e) => setNewVehicle({ ...newVehicle, submodel: e.target.value })}
              placeholder="e.g., SE, XLE"
            />
            <TextField
              label="VIN Number (Optional)"
              value={newVehicle.vin_number}
              onChange={(e) => updateVehicleDraft(setNewVehicle, "vin_number", e.target.value)}
              placeholder="17-character VIN"
              inputProps={{ maxLength: 17 }}
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddVehicleOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAddVehicle}
            disabled={!newVehicle.car_name.trim()}
          >
            Add Vehicle
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Vehicle Dialog */}
      <Dialog open={editVehicleOpen} onClose={() => setEditVehicleOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Vehicle for {customerFound?.first_name} {customerFound?.last_name}</DialogTitle>
        <DialogContent>
          <div style={{ display: "flex", flexDirection: "column", gap: "15px", paddingTop: "10px" }}>
            <TextField
              label="Vehicle Name *"
              value={editVehicle.car_name}
              onChange={(e) => setEditVehicle({ ...editVehicle, car_name: e.target.value })}
              placeholder="e.g., My Daily Driver, Work Truck"
              required
              error={!editVehicle.car_name.trim()}
              helperText={!editVehicle.car_name.trim() ? "Vehicle name is required" : ""}
            />
            <TextField
              label="Year"
              value={editVehicle.year}
              onChange={(e) => updateVehicleDraft(setEditVehicle, "year", e.target.value)}
              placeholder="e.g., 2020"
              inputProps={{ inputMode: "numeric", maxLength: 4 }}
            />
            <Autocomplete
              freeSolo
              options={makeOptions}
              loading={vehicleOptionsLoading.makes}
              value={editVehicle.make}
              onInputChange={(_, value) => updateVehicleDraft(setEditVehicle, "make", value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Make"
                  placeholder="e.g., Toyota"
                  helperText="Select from suggestions or type your own"
                />
              )}
            />
            <Autocomplete
              freeSolo
              options={editVehicleModelOptions}
              loading={vehicleOptionsLoading.editVehicleModels}
              value={editVehicle.model}
              onInputChange={(_, value) => updateVehicleDraft(setEditVehicle, "model", value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Model"
                  placeholder="e.g., Camry"
                  helperText="Select from suggestions or type your own"
                />
              )}
            />
            <TextField
              label="Submodel/Trim (Optional)"
              value={editVehicle.submodel}
              onChange={(e) => setEditVehicle({ ...editVehicle, submodel: e.target.value })}
              placeholder="e.g., SE, XLE"
            />
            <TextField
              label="VIN Number (Optional)"
              value={editVehicle.vin_number}
              onChange={(e) => updateVehicleDraft(setEditVehicle, "vin_number", e.target.value)}
              placeholder="17-character VIN"
              inputProps={{ maxLength: 17 }}
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditVehicleOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleEditVehicle}
            disabled={!editVehicle.car_name.trim()}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      <Footer />
    </>
  );
};

export default CreateTask;
